import React, { useEffect, useRef, useState } from 'react';
import { Check, Circle, Loader2 } from 'lucide-react';
import { PlanProgress } from '@models/projectPlanDraft.model';
import { derivePlanProgressRows, knownPhaseTotal, monotonicPhaseCount, planProgressRowLabel, PlanProgressRow } from '@utils/planProgress';
import { useTranslation } from '@hooks/useTranslation';

interface GeneratingStateProps {
  progress?: PlanProgress | null;
  /** The draft's attachment count; drives the "Reading attachments (i of N)" / "Read N" row. */
  attachmentCount?: number;
  /** Revisions never resend attachments, so the "reading" row is omitted. */
  isRevision?: boolean;
  /** Smaller variant shown above the last-good plan while a revision runs. */
  compact?: boolean;
  onBack?: () => void;
}

const SLOW_AFTER_MS = 90_000;

/**
 * Sprint 31, story-161 polling placeholder; Sprint 36, story-185 replaces the bare spinner with
 * a step list driven by `draft.progress`. Labels come from the step key (translated); the
 * server's `label` is only a fallback for an unknown step.
 */
export const GeneratingState: React.FC<GeneratingStateProps> = ({ progress, attachmentCount = 0, isRevision = false, compact = false, onBack }) => {
  const t = useTranslation('aiPlan');
  const [phaseCount, setPhaseCount] = useState(0);
  const [phaseTotal, setPhaseTotal] = useState(0);
  const [slow, setSlow] = useState(false);
  const stepStartedRef = useRef<{ key: string; at: number }>({ key: '', at: Date.now() });

  const stepKey = progress ? `${progress.step}` : 'none';

  useEffect(() => {
    setPhaseCount((prev) => monotonicPhaseCount(prev, progress));
    setPhaseTotal((prev) => knownPhaseTotal(prev, progress));
  }, [progress]);

  useEffect(() => {
    stepStartedRef.current = { key: stepKey, at: Date.now() };
    setSlow(false);
    const timer = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => clearTimeout(timer);
  }, [stepKey]);

  const rows = derivePlanProgressRows(progress, { hasAttachments: attachmentCount > 0, isRevision });

  const labelFor = (row: PlanProgressRow): string =>
    planProgressRowLabel(t, row, { progress, attachmentCount, phaseCount, phaseTotal });

  const currentRow = rows.find((r) => r.state === 'current');
  const currentLabel = currentRow ? labelFor(currentRow) : t.progressStarting;

  return (
    <div className={compact ? 'flex flex-col items-center gap-2 py-3 text-center' : 'flex flex-col items-center gap-4 py-12 text-center'}
      data-testid="ai-plan-generating-state">
      {!compact && <p className="text-base font-medium text-gray-900 dark:text-white">{t.generatingTitle}</p>}

      {!progress && (
        <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400" data-testid="ai-plan-progress-starting">
          <Loader2 className="w-4 h-4 animate-spin motion-reduce:animate-none" />
          {t.progressStarting}
        </div>
      )}

      {progress && (
        <ol className="w-full max-w-sm text-left space-y-2" data-testid="ai-plan-progress-list">
          {rows.map((row) => (
            <li
              key={row.key}
              data-testid={`ai-plan-progress-${row.key}`}
              data-state={row.state}
              aria-current={row.state === 'current' ? 'step' : undefined}
              className="flex items-center gap-3 text-sm">
              {row.state === 'done' && <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
              {row.state === 'current' && <Loader2 className="w-4 h-4 text-primary animate-spin motion-reduce:animate-none flex-shrink-0" />}
              {row.state === 'pending' && <Circle className="w-4 h-4 text-gray-300 dark:text-gray-600 flex-shrink-0" />}
              <span
                className={
                  row.state === 'pending'
                    ? 'text-gray-400 dark:text-gray-500'
                    : row.state === 'current'
                      ? 'font-medium text-gray-900 dark:text-white'
                      : 'text-gray-600 dark:text-gray-300'
                }>
                {labelFor(row)}
              </span>
            </li>
          ))}
        </ol>
      )}

      <p className="sr-only" aria-live="polite" data-testid="ai-plan-progress-live">
        {currentLabel}
      </p>

      {slow && <p className="text-xs text-gray-500 dark:text-gray-400">{t.progressSlow}</p>}
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md">{t.progressLeaveHint}</p>
      {onBack && (
        <button type="button" onClick={onBack} className="text-sm text-gray-500 underline">
          {t.commitCancel}
        </button>
      )}
    </div>
  );
};

export default GeneratingState;
